import { useEffect, useState } from "react";
import API from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import { CurrencyGbp, ArrowsClockwise, Tag, Users, Percent, Timer } from "@phosphor-icons/react";

// Platform admin: per-user price, annual discount, minimum seats and the sellable add-on price list
export default function PricingConfigPanel({ onSaved }) {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => { API.get("/platform/billing-config").then(({ data }) => setCfg(data)).catch(() => toast.error("Couldn't load pricing config")); }, []);

  const setAddon = (key, patch) => setCfg((c) => ({ ...c, addons: { ...c.addons, [key]: { ...c.addons[key], ...patch } } }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await API.put("/platform/billing-config", {
        per_user_monthly: parseFloat(cfg.per_user_monthly) || 0,
        annual_discount_pct: parseFloat(cfg.annual_discount_pct) || 0,
        min_seats: parseInt(cfg.min_seats, 10) || 1,
        dunning_grace_days: parseInt(cfg.dunning_grace_days, 10) || 14,
        trial_days: parseInt(cfg.trial_days, 10) || 14,
        addons: Object.fromEntries(Object.entries(cfg.addons).map(([k, v]) => [k, { monthly_price: parseFloat(v.monthly_price) || 0, sellable: !!v.sellable, trialable: v.trialable !== false }])),
      });
      setCfg((c) => ({ ...c, ...data, stripe_synced_at: data.stripe_synced ? new Date().toISOString() : c.stripe_synced_at }));
      toast.success(data.stripe_synced ? "Pricing saved and synced to Stripe" : "Pricing saved — Stripe sync will retry at next checkout");
      onSaved?.(data);
    } catch (err) { toast.error(err.response?.data?.detail || "Couldn't save pricing"); }
    finally { setSaving(false); }
  };

  const sync = async () => {
    setSyncing(true);
    try { const { data } = await API.post("/platform/billing-config/sync-stripe"); toast.success(`Stripe catalogue synced (${data.prices} prices)`); setCfg((c) => ({ ...c, stripe_synced_at: new Date().toISOString() })); }
    catch (err) { toast.error(err.response?.data?.detail || "Stripe sync failed"); }
    finally { setSyncing(false); }
  };

  if (!cfg) return <div className="border border-border rounded-lg p-6 text-xs text-muted-foreground" data-testid="pricing-config-loading">Loading pricing…</div>;

  return (
    <div className="border border-border rounded-lg overflow-hidden mb-6" data-testid="pricing-config-panel">
      <div className="px-4 py-3 bg-muted/30 border-b border-border flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Tag size={14} className="text-muted-foreground" />
          <h3 className="text-sm font-semibold font-[Chivo]">Pricing & Sellable Add-ons</h3>
          <Badge variant="secondary" className="text-[9px]">{cfg.stripe_synced_at ? `Stripe synced ${new Date(cfg.stripe_synced_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}` : "Not yet synced to Stripe"}</Badge>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="text-xs h-7" onClick={sync} disabled={syncing} data-testid="pricing-sync-stripe-btn"><ArrowsClockwise size={12} className={`mr-1 ${syncing ? "animate-spin" : ""}`} /> Sync Stripe</Button>
          <Button size="sm" className="text-xs h-7" onClick={save} disabled={saving} data-testid="pricing-save-btn">{saving ? "Saving…" : "Save pricing"}</Button>
        </div>
      </div>
      <div className="p-4 grid lg:grid-cols-[260px_1fr] gap-6">
        <div className="space-y-3">
          <div>
            <Label className="text-xs flex items-center gap-1"><Users size={12} /> Price per user / month</Label>
            <div className="relative mt-1"><CurrencyGbp size={14} className="absolute left-2.5 top-2.5 text-muted-foreground" /><Input type="number" step="0.5" min="0" className="pl-7" value={cfg.per_user_monthly} onChange={(e) => setCfg({ ...cfg, per_user_monthly: e.target.value })} data-testid="pricing-per-user" /></div>
          </div>
          <div>
            <Label className="text-xs flex items-center gap-1"><Percent size={12} /> Annual discount</Label>
            <Input type="number" step="1" min="0" max="90" className="mt-1" value={cfg.annual_discount_pct} onChange={(e) => setCfg({ ...cfg, annual_discount_pct: e.target.value })} data-testid="pricing-annual-discount" />
            <p className="text-[10px] text-muted-foreground mt-1">Applied to the whole basket when a tenant pays yearly.</p>
          </div>
          <div>
            <Label className="text-xs">Minimum seats</Label>
            <Input type="number" min="1" className="mt-1" value={cfg.min_seats} onChange={(e) => setCfg({ ...cfg, min_seats: e.target.value })} data-testid="pricing-min-seats" />
          </div>
          <div>
            <Label className="text-xs">Dunning grace (days)</Label>
            <Input type="number" min="1" max="90" className="mt-1" value={cfg.dunning_grace_days ?? 14} onChange={(e) => setCfg({ ...cfg, dunning_grace_days: e.target.value })} data-testid="pricing-dunning-grace" />
            <p className="text-[10px] text-muted-foreground mt-1">After a failed renewal, the workspace goes read-only this many days later until the card is fixed.</p>
          </div>
          <div>
            <Label className="text-xs flex items-center gap-1"><Timer size={12} /> Free trial length (days)</Label>
            <Input type="number" min="1" max="90" className="mt-1" value={cfg.trial_days ?? 14} onChange={(e) => setCfg({ ...cfg, trial_days: e.target.value })} data-testid="pricing-trial-days" />
            <p className="text-[10px] text-muted-foreground mt-1">Workspace admins can switch a locked add-on on for this long, once per add-on. Applies to trials started from now on; use the Free trial column to switch trials off for an add-on.</p>
          </div>
          <p className="text-[10px] text-muted-foreground">Core modules (jobs, quotes, invoices, clients, suppliers, projects, reports…) are always included. Everything below is sold as an add-on.</p>
        </div>
        <div className="border border-border rounded-md overflow-hidden">
          <table className="w-full text-xs">
            <thead><tr className="bg-muted/40 text-[10px] uppercase tracking-wider text-muted-foreground"><th className="text-left px-3 py-2 font-semibold">Add-on</th><th className="text-left px-3 py-2 font-semibold w-36">£ / month</th><th className="text-left px-3 py-2 font-semibold w-24">Sellable</th><th className="text-left px-3 py-2 font-semibold w-24">Free trial</th></tr></thead>
            <tbody className="divide-y divide-border">
              {cfg.addon_meta.map((a) => (
                <tr key={a.key} data-testid={`pricing-addon-${a.key}`}>
                  <td className="px-3 py-2"><p className="font-semibold">{a.label}</p><p className="text-[10px] text-muted-foreground leading-snug">{a.blurb}</p></td>
                  <td className="px-3 py-2"><Input type="number" step="0.5" min="0" className="h-8" value={cfg.addons[a.key]?.monthly_price ?? ""} onChange={(e) => setAddon(a.key, { monthly_price: e.target.value })} data-testid={`pricing-addon-price-${a.key}`} /></td>
                  <td className="px-3 py-2"><Switch checked={cfg.addons[a.key]?.sellable !== false} onCheckedChange={(v) => setAddon(a.key, { sellable: v })} data-testid={`pricing-addon-sellable-${a.key}`} /></td>
                  <td className="px-3 py-2"><Switch checked={cfg.addons[a.key]?.trialable !== false} onCheckedChange={(v) => setAddon(a.key, { trialable: v })} data-testid={`pricing-addon-trialable-${a.key}`} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
